import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ConfigDataResponse,
    DatosABuscarTicket,
    Evento,
    GetTicketsByCuitPrestadorProperties,
    GetTicketsByIDProperties,
    GetTicketsByParameters,
    GetTicketsByPrestadorLugarAtencionProperties,
    NewTicketBody,
    OperadoresData,
    PSToolErrorResponseModel,
    PSToolFinanciadoresResponse,
    PSToolResponseModel,
    ReclamoBackendData,
    TicketBodyInformation,
    UpdateOperatorTicket,
    UpdateTicketBody,
    UpdateTicketData,
} from 'app/interfaces/reclamos.interface';
import { AuthService } from '../core/auth/auth.service';
import { environment } from 'environments/environment';
import { AppState } from 'app/store/app.reducer';
import { Store } from '@ngrx/store';
import {
    BasicResponseInterface,
    BasicResponseModel,
} from 'app/interfaces/transcodificaciones.interface';
import { LugarAtencionInterface, Prestador } from 'app/interfaces/prestadorLugarDeAtencion.interface';

@Injectable({
    providedIn: 'root',
})
export class ClaimService {
    private apiURL = environment.ticketURL;
    private psToolURL = environment.url;

    constructor(
        private _httpClient: HttpClient,
        private _authService: AuthService,
        private _store: Store<AppState>
    ) {}

    /**
     * Obtiene los tickets existentes para un prestador específico.
     * @param ticketSearchProperties número de CUIT del prestador
     * @param page número de la página que se quiere obtener
     * @param size tamaño de la página que se quiere obtener
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByParameters(
        { cuitPrestador, idEstado, numeroReclamo, lugarAtencion, idCategoria, emailOperador, etiquetas, descripcion, descripcionSolucion, fechaDesde, fechaHasta }: GetTicketsByParameters,
        page: number,
        size: number
    ): Promise<ReclamoBackendData> {

        let url: string = `${this.apiURL}/tickets?aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
        if (cuitPrestador) url = url +`&cuitPrestador=${cuitPrestador}`;
        if (idEstado) url = url+`&estadoId=${idEstado}`;
        if (numeroReclamo) url = url+`&id=${numeroReclamo}`;
        if (lugarAtencion) url = url+`&lugarAtencion=${lugarAtencion}`
        if (idCategoria) url = url+`&categoriaId=${idCategoria}`
        if (descripcion) url = url+`&descripcion=${descripcion}`
        if (descripcionSolucion) url = url+`&descripcionEstado=${descripcionSolucion}`
        if (emailOperador) url = url+`&emailOperador=${emailOperador}`
        if (fechaDesde) url = url+`&fechaDesde=${fechaDesde}`
        if (fechaHasta) url = url+`&fechaHasta=${fechaHasta}`
        if (etiquetas && etiquetas.length > 0) {
            const params = etiquetas.map((id)=> '&etiqueta=' + id)
            url = url + params.join('')
        }

        return new Promise<ReclamoBackendData>((resolve, reject) => {
            this._httpClient
                .get(url, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     * Obtiene el ticket (si existe) que tiene el mismo número de id que el que se está solicitando
     * @param id número de ticket o id del ticket
     * @param page número de la página que se quiere obtener
     * @param size tamaño de la página que se quiere obtener
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByID(
        { numeroReclamo, idEstado }: GetTicketsByIDProperties,
        page: number,
        size: number
    ): Promise<ReclamoBackendData> {
        return new Promise<ReclamoBackendData>((resolve, reject) => {
            let url: string = `${this.apiURL}/tickets?id=${numeroReclamo}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
            if (idEstado)
                url = `${this.apiURL}/tickets?id=${numeroReclamo}&estadoId=${idEstado}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
            this._httpClient
                .get(url, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     * Obtiene el ticket (si existe) que tiene el mismo número de id que el que se está solicitando
     * @param idEstado id del estado del ticket
     * @param page número de la página que se quiere obtener
     * @param size tamaño de la página que se quiere obtener
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByStatusID(idEstado: number, page: number, size: number): Promise<ReclamoBackendData>  {
        return new Promise<ReclamoBackendData>((resolve, reject) => {
            let url: string = `${this.apiURL}/tickets?estadoId=${idEstado}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`
            this._httpClient.get(url, this._authService.headers_http).subscribe(
                {
                    next: (response: ReclamoBackendData) =>
                    {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    }
                }
            )
        })
    }

    /**
     * Obtiene el ticket (si existe) que tiene el mismo número de id que el que se está solicitando
     * @param id número de ticket o id del ticket
     * @param page número de la página que se quiere obtener
     * @param size tamaño de la página que se quiere obtener
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByCategoryID(
        idCategoria: number,
        page: number,
        size: number,
        fechaDesde?: string,
        fechaHasta?: string
    ): Promise<ReclamoBackendData> {
        return new Promise<ReclamoBackendData>((resolve, reject) => {
            let url: string = `${this.apiURL}/tickets?categoriaId=${idCategoria}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
            if(fechaDesde) url = url + `&fechaDesde=${fechaDesde}`
            if(fechaHasta) url = url + `&fechaHasta=${fechaHasta}`
            this._httpClient
                .get(url, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     * Obtiene los tickets existentes para un prestador específico.
     * @param cuitPrestador número de CUIT del prestador
     * @param page número de la página que se quiere obtener
     * @param size tamaño de la página que se quiere obtener
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByCUITPrestador(
        { cuitPrestador, idEstado, fechaDesde, fechaHasta }: GetTicketsByCuitPrestadorProperties,
        page: number,
        size: number
    ): Promise<ReclamoBackendData> {
        let url: string = `${this.apiURL}/tickets?cuitPrestador=${cuitPrestador}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
        if (idEstado)
            url = `${this.apiURL}/tickets?cuitPrestador=${cuitPrestador}&estadoId=${idEstado}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
        if(fechaDesde) url = url + `&fechaDesde=${fechaDesde}`
        if(fechaHasta) url = url + `&fechaHasta=${fechaHasta}`
        return new Promise<ReclamoBackendData>((resolve, reject) => {
            this._httpClient
                .get(url, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     * Obtiene los tickets existentes para un prestador en un lugar de atención particular.
     * @param cuitPrestador número de CUIT del prestador
     * @param lugarAtencion el domicilio del lugar de atención
     * @param page número de la página que se quiere obtener
     * @param size tamaño de la página que se quiere obtener
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByCUITPrestadorYLugarAtencion(
        {
            cuitPrestador,
            lugarAtencion,
            idEstado,
            fechaDesde,
            fechaHasta,
        }: GetTicketsByPrestadorLugarAtencionProperties,
        page: number,
        size: number
    ): Promise<ReclamoBackendData> {
        return new Promise<ReclamoBackendData>((resolve, reject) => {
            let url = `${this.apiURL}/tickets?cuitPrestador=${cuitPrestador}&aplicacion=PSTool,HolaSuite&page=${page}&size=${size}`;
            if (lugarAtencion !== 'Todos')
                url = url + `&lugarAtencion=${lugarAtencion}`;
            if (idEstado) url = url + `&estadoId=${idEstado}`;
            if (fechaDesde) url = url + `&fechaDesde=${fechaDesde}`;
            if (fechaHasta) url = url + `&fechaHasta=${fechaHasta}`;

            this._httpClient
                .get(url, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     * Obtiene los tickets creados para un beneficiario en base a su número de CUIT.
     * @param cuitBeneficiario número de CUIT del beneficiario.
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getTicketsByCUITBeneficiario(
        cuitBeneficiario: number | string
    ): Promise<ReclamoBackendData> {
        return new Promise<ReclamoBackendData>((resolve, reject) => {
            this._httpClient
                .get(
                    `${this.apiURL}/tickets?cuit=${cuitBeneficiario}&aplicacion=PSTool,HolaSuite`,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     * Obtiene los valores de configuración inicial para un reclamo.
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    getConfigData(): Promise<ConfigDataResponse> {
        return new Promise<ConfigDataResponse>((resolve, reject) => {
            this._httpClient
                .get(
                    `${this.apiURL}/configuraciones`,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: ConfigDataResponse) => {
                        console.log('response', response);
                        resolve(response);
                    },
                    error: (errorResponse: ReclamoBackendData) => {
                        console.log('errorResponse', errorResponse);
                        reject(errorResponse);
                    },
                });
        });
    }

    /**
     *  Actualiza el estado de un ticket ya creado.
     *
     * _Actualmente no hay validaciones en el backend respecto al orden de los estados que se puedan mandar. Lo que sea que se mande desde el front, será aplicado en el backend._
     * @param ticketId id del ticket al que se le desea cambiar el estado.
     * @param appName nombre de la aplicación desde la cual se está creando el ticket.
     * @param body JSON con la información necesaria para poder modificar el estado del ticket.
     * @param idTipoSolucion es el id del tipo de solución elegida. Es un parámetro opcional que se envía sólo cuando se cambia el estado a _Resuelto_.
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    updateTicketStatus(
        ticketId: string,
        appName: string,
        body: UpdateTicketBody,
        idTipoSolucion?: number
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            let url = `${this.apiURL}/tickets/estados?id=${ticketId}&aplicacion=${appName}`;
            if (idTipoSolucion) {
                url = `${this.apiURL}/tickets/estados?id=${ticketId}&aplicacion=${appName}&idTipoSolucion=${idTipoSolucion}`;
            }
            this._httpClient
                .put(url, body, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('update ticket response', response);
                        resolve(response);
                    },
                    error: (error: ReclamoBackendData) => {
                        console.log('update ticket responseError', error);
                        reject(error);
                    },
                });
        });
    }

    /**
     * Asocia un evento a un ticket ya existente. Se llama _evento_ a cualquier aclaración que se desee realizar sobre un ticket ya creado o a una acción intermedia que se realice para poder 'cerrar' o 'resolver' un ticket.
     * @param ticketId id del ticke al que se va a asociar el evento nuevo.
     * @param body un JSON con la información necesaria para poder crear un evento.
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    addNewEvent(ticketId: string, body: Evento): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this._httpClient
                .post(
                    `${this.apiURL}/tickets/eventos?idTicket=${ticketId}`,
                    body,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('new event response', response);
                        resolve(response);
                    },
                    error: (error: ReclamoBackendData) => {
                        console.log('new event responseError', error);
                        reject(error);
                    },
                });
        });
    }

    /**
     * Hace el POST de un nuevo ticket
     * @param body un JSON que contiene la información requerida para la creación de un ticket.
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    createNewTicket(body: NewTicketBody): Promise<any> {
        console.log({ body });

        return new Promise<any>((resolve, reject) => {
            this._httpClient
                .post(
                    `${this.apiURL}/tickets`,
                    body,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('created ticket response', response);
                        resolve(response);
                    },
                    error: (error: any) => {
                        console.log('created ticket responseError', error);
                        reject(error);
                    },
                });
        });
    }

    /**
     * Hace el POST de un nuevo ticket
     * @param body un JSON que contiene la información requerida para la creación de un ticket.
     * @returns una promesa con la respuesta que nos envía el backend de PSTicket.
     */
    createNewTickets(body: NewTicketBody[]): Promise<any> {
        console.log({ body });

        return new Promise<any>((resolve, reject) => {
            this._httpClient
                .post(
                    `${this.apiURL}/tickets/masivo`,
                    body,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('created ticket response', response);
                        resolve(response);
                    },
                    error: (error: any) => {
                        console.log('created ticket responseError', error);
                        reject(error);
                    },
                });
        });
    }

    /**
     * Obtiene los prestadores existentes en PSTool que tengan el número de CUIT solicitado
     * @param cuit número de CUIT del prestador.
     * @returns una promesa con la respuesta que nos envía el backend de PSTool.
     */
    getPrestadoresByCUIT(
        cuit: string | number
    ): Promise<PSToolResponseModel | PSToolErrorResponseModel> {
        return new Promise<PSToolResponseModel | PSToolErrorResponseModel>(
            (resolve, reject) => {
                this._httpClient
                    .get(
                        `${this.psToolURL}/prestadores?cuit=${cuit}&numeroPagina=1&cantidadPorPagina=10&detalle=true`,
                        this._authService.headers_http
                    )
                    .subscribe({
                        next: (response: PSToolResponseModel) => {
                            console.log({ response });
                            resolve(response);
                        },
                        error: (err: PSToolErrorResponseModel) => {
                            console.log({ error: err });
                            reject(err);
                        },
                    });
            }
        );
    }

    /**
     * Obtiene los lugares de atención existentes en PSTool que tengan la licencia solicitada
     * @param licencia número de licencia del lugar de atención.
     * @returns una promesa con la respuesta que nos envía el backend de PSTool.
     */
    getLugarDeAtencionYPrestadoresByLicencia(
        licencia: string | number
    ): Promise<
        BasicResponseInterface<BasicResponseModel<LugarAtencionInterface[] | []> | string>
    > {
        return new Promise<
            BasicResponseInterface<BasicResponseModel<LugarAtencionInterface[] | []> | string>
        >((resolve, reject) => {
            this._httpClient
                .get(
                    `${this.psToolURL}/lugaresAtencion?licencia=${licencia}&numeroPagina=1&cantidadPorPagina=10&detalle=true`,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (
                        response: BasicResponseInterface<
                            BasicResponseModel<LugarAtencionInterface[] | []>
                        >
                    ) => {
                        console.log({ response });
                        resolve(response);
                    },
                    error: (err: BasicResponseInterface<string>) => {
                        console.log({ error: err });
                        reject(err);
                    },
                });
        });
    }

    /**
     * Obtiene los lugares de atención existentes en PSTool que tengan la licencia solicitada
     * @param filial código de la filial
     * @param codigoPrestadorParaFinanciador código del prestador para el financiador
     * @returns una proesa con la respuesta que obtenemos desde el backend
     */
    getLugarDeAtencionYPrestadoresByFilialYCodigoPrestador(
        filial: number,
        codigoPrestadorParaFinanciador: number
    ): Promise<
        BasicResponseInterface<BasicResponseModel<LugarAtencionInterface[] | []> | string>
    > {
        return new Promise<
            BasicResponseInterface<BasicResponseModel<LugarAtencionInterface[] | []> | string>
        >((resolve, reject) => {
            this._httpClient
                .get(
                    `${this.psToolURL}/lugaresAtencion?codigoFilial=${filial}&codigoParaFinanciador=${codigoPrestadorParaFinanciador}&numeroPagina=1&cantidadPorPagina=10&detalle=true`,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (
                        response: BasicResponseInterface<
                            BasicResponseModel<LugarAtencionInterface[] | []>
                        >
                    ) => {
                        console.log({ response });
                        resolve(response);
                    },
                    error: (err: BasicResponseInterface<string>) => {
                        console.log({ error: err });
                        reject(err);
                    },
                });
        });
    }

    /**
     * Obtiene los financiadores existentes en PSTool
     * @returns una promesa con la respuesta que nos envía el backend de PSTool.
     */
    getFinanciadores(): Promise<
        PSToolFinanciadoresResponse | PSToolErrorResponseModel
    > {
        return new Promise<
            PSToolFinanciadoresResponse | PSToolErrorResponseModel
        >((resolve, reject) => {
            this._httpClient
                .get(
                    `${this.psToolURL}/financiadores`,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: PSToolFinanciadoresResponse) => {
                        console.log({ response });
                        resolve(response);
                    },
                    error: (err: PSToolErrorResponseModel) => {
                        console.log({ error: err });
                        reject(err);
                    },
                });
        });
    }

    async generateNewTicketBody(
        ticketData: TicketBodyInformation
    ): Promise<NewTicketBody[] | null> {
        console.log('ticketData recibido', ticketData);

        let userEmail: string;
        this._store.select('auth').subscribe((auth) => {
            userEmail = auth.auth.userEmail;
        });

        let lugaresAtencionEncontrados: BasicResponseInterface<
            BasicResponseModel<[] | LugarAtencionInterface[]> | string
        >;
        const informacionEncontrada = await this.getConfigDataIDs(
            ticketData.categoriaABuscar || null,
            ticketData.tipoSolucionABuscar || null,
            ticketData.estadoABuscar
        );

        let bodyArrays: NewTicketBody[] = [];
        let body: NewTicketBody = {
            cerrado: ticketData.cerrado,
            categoria: {
                id: ticketData.categoria || informacionEncontrada.categoria,
            },
            aplicacion: {
                id: 7,
            },
            operador: ticketData.emailOperador == undefined ? null :  {
                email:  ticketData.emailOperador || userEmail,
            },
            usuario: {
                email: ticketData.usuarioEmail || null,
                nombre: ticketData.usuarioNombre || null,
                telefono: ticketData.usuarioTelefono || null,
            },
            prestador: {
                cuit: ticketData.cuitPrestador,
                lugarAtencion: ticketData.lugarAtencion || null,
                financiador: ticketData.financiador,
            },
            descripcion: ticketData.descripcion,
            estados: [
                {
                    id: informacionEncontrada.estado,
                },
            ],
            etiquetas: ticketData.etiquetas || null,
        };

        if (ticketData.cerrado == true) {
            body = {
                ...body,
                estados: [
                    {
                        id: informacionEncontrada.estado,
                        cambioEstadoDescripcion: ticketData.descripcionSolucion || ticketData.descripcion,
                    },
                ],
                tipoSolucion: {
                    id:
                        ticketData.tipoSolucion ||
                        informacionEncontrada.solucion,
                },
            };
        }

        if (
            ticketData.buscarLugaresAtencionPorLicencia &&
            ticketData.licencia
        ) {
            lugaresAtencionEncontrados =
                await this.getLugarDeAtencionYPrestadoresByLicencia(
                    ticketData.licencia
                );
            console.log({ lugaresAtencionEncontrados });
        }

        if (typeof lugaresAtencionEncontrados?.response === 'string')
            return new Promise<NewTicketBody[]>((resolve, reject) => {
                reject(null);
            });

        if (lugaresAtencionEncontrados?.response.contenido.length > 0) {
            lugaresAtencionEncontrados.response.contenido.forEach(
                (lugarAtencion: LugarAtencionInterface) => {
                    console.log(lugarAtencion);
                    const lugarDeAtencion = lugarAtencion.domicilio;
                    if (ticketData.cuitPrestador !== null) {
                        let body1: NewTicketBody = {
                            cerrado: true,
                            categoria: {
                                id: informacionEncontrada.categoria,
                            },
                            aplicacion: {
                                id: 7,
                            },
                            operador: {
                                email: ticketData.emailOperador || userEmail,
                            },
                            usuario: {
                                email: null,
                                nombre: null,
                                telefono: null,
                            },
                            prestador: {
                                cuit: ticketData.cuitPrestador,
                                lugarAtencion: lugarDeAtencion,
                                financiador: ticketData.financiador,
                            },
                            descripcion: ticketData.descripcion,
                            estados: [
                                {
                                    id: informacionEncontrada.estado,
                                    cambioEstadoDescripcion: ticketData.descripcionSolucion || ticketData.descripcion,
                                },
                            ],
                            tipoSolucion: {
                                id: informacionEncontrada.solucion,
                            },
                        };
                        console.log({ body1 });

                        bodyArrays.push(body1);
                    } else {
                        lugarAtencion.prestadores.forEach(
                            (prestador: Prestador) => {
                                let body2: NewTicketBody = {
                                    cerrado: true,
                                    categoria: {
                                        id: informacionEncontrada.categoria,
                                    },
                                    aplicacion: {
                                        id: 7,
                                    },
                                    operador: {
                                        email: ticketData.emailOperador || userEmail,
                                    },
                                    usuario: {
                                        email: null,
                                        nombre: null,
                                        telefono: null,
                                    },
                                    prestador: {
                                        cuit: prestador.cuit || null,
                                        lugarAtencion: lugarDeAtencion,
                                        financiador: ticketData.financiador,
                                    },
                                    descripcion: ticketData.descripcion,
                                    estados: [
                                        {
                                            id: informacionEncontrada.estado,
                                            cambioEstadoDescripcion: ticketData.descripcionSolucion || ticketData.descripcion,
                                        },
                                    ],
                                    tipoSolucion: {
                                        id: informacionEncontrada.solucion,
                                    },
                                };
                                console.log({ body2 });
                                bodyArrays.push(body2);
                            }
                        );
                    }
                }
            );
        }

        console.log(
            'ticketData.buscarLugaresAtencionPorLicencia',
            ticketData.buscarLugaresAtencionPorLicencia
        );
        console.log(
            'ticketData.buscarLicenciasPorFilialYCodigoPrestador',
            ticketData.buscarLicenciasPorFilialYCodigoPrestador
        );

        if (!ticketData.buscarLugaresAtencionPorLicencia) {
            console.log('entra al ultimo if');

            bodyArrays.push(body);
        }

        return new Promise<NewTicketBody[]>((resolve, reject) => {
            if (informacionEncontrada) {
                console.log('inside if', { bodyArrays });

                // TODO definir en base a qué devuelve todo OK
                resolve(bodyArrays);
            }
            console.log('after if');

            // TODO definir en base a qué devuelve el null
            reject(null);
        });
    }

    async getConfigDataIDs(
        nombreCategoria: string | null,
        nombreSolucion: string | null,
        nombreEstado: string
    ): Promise<DatosABuscarTicket> {
        let idCategoriaEncontrada: string | number = null;
        let idTipoSolucionEncontrada: string | number = null;
        let idEstadoEncontrado: string | number;

        await this.getConfigData().then((response) => {
            console.log('config data response', response);
            if (nombreCategoria !== null) {
                idCategoriaEncontrada = response.categorias.find(
                    (categoria) => categoria.nombre == nombreCategoria
                ).id;
            }

            if (nombreSolucion !== null) {
                idTipoSolucionEncontrada = response.tipoSoluciones.find(
                    (tipoSolucion) => tipoSolucion.nombre == nombreSolucion
                ).id;
            }

            idEstadoEncontrado = response.estados.find(
                (estado) => estado.estado == nombreEstado
            ).id;
        });

        return {
            categoria: idCategoriaEncontrada,
            solucion: idTipoSolucionEncontrada,
            estado: idEstadoEncontrado,
        };
    }

    getCurrentOperators(): Promise<BasicResponseInterface<OperadoresData[] | string>> {
        return new Promise<BasicResponseInterface<OperadoresData[] | string>>((resolve, reject) => {
            this._httpClient
                .get(
                    `${this.apiURL}/operadores`,
                    this._authService.headers_http
                )
                .subscribe({
                    next: (response: BasicResponseInterface<OperadoresData[]>) => {
                        console.log({ response });
                        resolve(response);
                    },
                    error: (err: BasicResponseInterface<string>) => {
                        console.log({ error: err });
                        reject(err);
                    },
                });
        });
    }

    updateTicketOperator(
        ticketId: string,
        body: UpdateOperatorTicket ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            let url = `${this.apiURL}/tickets/operadores?id=${ticketId}`;

            this._httpClient
                .put(url, body, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('update ticket operator response', response);
                        resolve(response);
                    },
                    error: (error: ReclamoBackendData) => {
                        console.log('update ticket operator responseError', error);
                        reject(error);
                    },
                });
        });
    }

    updateTicketData(
        ticketId: string,
        body: UpdateTicketData ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            let url = `${this.apiURL}/tickets/${ticketId}`;

            this._httpClient
                .put(url, body, this._authService.headers_http)
                .subscribe({
                    next: (response: ReclamoBackendData) => {
                        console.log('update ticket data response', response);
                        resolve(response);
                    },
                    error: (error: ReclamoBackendData) => {
                        console.log('update ticket data responseError', error);
                        reject(error);
                    },
                });
        });
    }
}
